import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Context for global notification access
const NotificationContext = createContext();

// Custom hook to use notifications
export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

// Simple notification component
const SimpleNotification = ({ notification, onClose }) => {
  const getNotificationStyles = (type) => {
    switch (type) {
      case 'success':
        return 'bg-cheese text-crust border-l-4 border-pepperoni';
      case 'error':
        return 'bg-pepperoni text-accentwhite border-l-4 border-cheese';
      case 'warning':
        return 'bg-secondary-500 text-crust border-l-4 border-pepperoni';
      case 'info':
        return 'bg-olive text-accentwhite border-l-4 border-cheese';
      default:
        return 'bg-olive-light text-accentwhite border-l-4 border-cheese';
    }
  };

  return (
    <div className={`p-4 rounded-xl shadow-lg w-full font-sans ${getNotificationStyles(notification.type)}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="font-bold text-lg mb-1">{notification.title}</div>
          <div className="text-sm opacity-90">{notification.content}</div>
          {notification.details && (
            <div className="text-xs opacity-75 mt-2">{notification.details}</div>
          )}
        </div>
        <button
          onClick={onClose}
          className="ml-3 hover:opacity-75 transition-opacity duration-200 text-xl font-bold"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

// Notification Provider Component
export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const showMessage = useCallback((type, title, content, details = null, duration = 5000) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const newNotification = { id, type, title, content, details };
    
    setNotifications(prev => [newNotification, ...prev].slice(0, 3)); // Keep max 3 notifications
    
    if (duration > 0) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, duration);
    }
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  // Global access via window
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.showMessage = showMessage;
      window.showNotification = showMessage; // Keep backward compatibility
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete window.showMessage;
        delete window.showNotification;
      }
    };
  }, [showMessage]);

  const value = {
    showMessage,
    removeNotification,
    notifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationDisplay 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />
    </NotificationContext.Provider>
  );
};

// Notification Display Component
const NotificationDisplay = ({ notifications, removeNotification }) => {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 640;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (notifications.length === 0) return null;

  return createPortal(
    <div
      className="fixed z-[999999] space-y-3 pointer-events-none"
      style={
        isMobile
          ? {
              top: '1rem',
              left: '1rem',
              right: '1rem',
              bottom: 'auto'
            }
          : {
              top: '1rem',
              right: '1rem',
              bottom: 'auto',
              left: 'auto',
              width: '400px'
            }
      }
    >
      <AnimatePresence>
        {notifications.map((notification, index) => (
          <motion.div
            key={notification.id}
            initial={
              isMobile
                ? { opacity: 0, y: -50, scale: 0.95 }
                : { opacity: 0, x: 100, scale: 0.95 }
            }
            animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
            exit={
              isMobile
                ? { opacity: 0, y: -50, scale: 0.95 }
                : { opacity: 0, x: 100, scale: 0.95 }
            }
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="pointer-events-auto w-full"
            style={{ zIndex: 999999 - index }}
          >
            <SimpleNotification
              notification={notification}
              onClose={() => removeNotification(notification.id)}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>,
    document.body
  );
};

// Backward compatible utility functions (same API as before)
export const showSuccessNotification = (message, options = {}) => {
  if (window.showMessage) {
    window.showMessage(
      'success', 
      options.title || 'Success', 
      message, 
      options.details || null,
      options.duration || 5000
    );
  } else {
    console.warn('NotificationProvider not mounted. Cannot show success notification:', message);
  }
};

export const showErrorNotification = (message, options = {}) => {
  if (window.showMessage) {
    window.showMessage(
      'error', 
      options.title || 'Error', 
      message, 
      options.details || null,
      options.duration || 5000
    );
  } else {
    console.warn('NotificationProvider not mounted. Cannot show error notification:', message);
  }
};

export const showWarningNotification = (message, options = {}) => {
  if (window.showMessage) {
    window.showMessage(
      'warning', 
      options.title || 'Warning', 
      message, 
      options.details || null,
      options.duration || 5000
    );
  } else {
    console.warn('NotificationProvider not mounted. Cannot show warning notification:', message);
  }
};

export const showInfoNotification = (message, options = {}) => {
  if (window.showMessage) {
    window.showMessage(
      'info', 
      options.title || 'Info', 
      message, 
      options.details || null,
      options.duration || 5000
    );
  } else {
    console.warn('NotificationProvider not mounted. Cannot show info notification:', message);
  }
};

export const showBackendNotification = (backendNotification) => {
  if (window.showMessage && backendNotification) {
    // Handle backend notification format
    const type = backendNotification.type || 'info';
    const title = backendNotification.title || type.charAt(0).toUpperCase() + type.slice(1);
    const message = backendNotification.message || 'No message provided';
    const details = backendNotification.details || null;
    const duration = backendNotification.duration ? backendNotification.duration * 1000 : 5000;
    
    window.showMessage(type, title, message, details, duration);
  } else {
    console.warn('NotificationProvider not mounted. Cannot show backend notification:', backendNotification);
  }
};

export default NotificationProvider;